import React, { Suspense } from 'react';
import { CenteredLoadingIndicator } from '@spotify-internal/anchor-ds';
import { detectLocale } from 'i18n/detectLocale';
import * as serverRenderingUtils from 'helpers/serverRenderingUtils';

const developmentHostnames = ['localhost', 'development.anchor.fm'];

type LocaleProviderValue = {
  changeLocale: (newLocale: string) => void;
  enableI18N: boolean;
  locale: string;
};

export const LocaleContext = React.createContext<LocaleProviderValue>({
  changeLocale: () => {},
  enableI18N: false,
  locale: 'en',
});

export function LocaleProvider({
  locale = 'en',
  children,
}: {
  locale?: string;
  children: React.ReactNode;
}): React.ReactElement {
  // enableI18N Flag for 2022 i18n Launch (local and dev only)
  const enableI18N =
    !serverRenderingUtils.windowUndefined() &&
    developmentHostnames.includes(window.location.hostname);
  const [resolvedLocale, setResolvedLocale] = React.useState<string>(locale);

  const changeLocale = (requestedLocale: string) => {
    // Safety check to not run the changeLanguage function when unneccessary
    if (requestedLocale === resolvedLocale) {
      /* eslint-disable-next-line no-console */
      console.log('The preferred language is already set: ', resolvedLocale);
      return;
    }

    setResolvedLocale(requestedLocale);
  };

  React.useEffect(() => {
    // Only run this if the window exists && we are not running unit tests. The unit test browser
    // is always set to 'en' so it will try to override whatever we pass to the LocaleProvider.
    // This does not effect cypress tests as the process.env.NODE_ENV is "development"
    if (!process.env.NODE_ENV?.includes('test'))
      setResolvedLocale(detectLocale());
  }, [!serverRenderingUtils.windowUndefined()]);

  return (
    <LocaleContext.Provider
      value={{
        locale: resolvedLocale,
        enableI18N,
        changeLocale,
      }}
    >
      <Suspense
        fallback={
          <div>
            <CenteredLoadingIndicator size="lg" />
          </div>
        }
      >
        {children}
      </Suspense>
    </LocaleContext.Provider>
  );
}
